import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Scroll, Hammer, Globe, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';

import { useTranslation } from 'react-i18next';

import { useSafeBack } from '@/utils/useSafeBack';
const TYPE_META = {
  historical:  { label: 'Historical',  color: '#60a5fa', icon: Scroll },
  cultural:    { label: 'Cultural',    color: '#f472b6', icon: Globe },
  manuscript:  { label: 'Manuscript',  color: '#facc15', icon: BookOpen },
  archaeology: { label: 'Archaeology', color: '#fb923c', icon: Hammer },
};

function TypeBadge({ type }) {
  const meta = TYPE_META[type] || TYPE_META.historical;
  const Icon = meta.icon;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide"
      style={{
        background: `${meta.color}1F`,
        color: meta.color,
        border: `1px solid ${meta.color}40`,
        letterSpacing: '0.06em',
      }}
    >
      <Icon className="h-2.5 w-2.5" />
      {meta.label}
    </span>
  );
}

export default function SourcesPage() {
  const {
    t
  } = useTranslation();

  const navigate = useNavigate();
  const goBackSafe = useSafeBack('/home');
  const [sources, setSources] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchSources() {
      try {
        const res = await axios.get(`${API}/historical-sources`);
        setSources(res.data.sources || []);
        setTotalEntries(res.data.total_entries || 0);
        setNote(res.data.note || '');
      } catch {
        // Graceful empty state
      } finally {
        setLoading(false);
      }
    }
    fetchSources();
  }, []);

  return (
    <div className="page-container" style={{ background: 'var(--theme-background)', color: 'var(--theme-text)' }}>
      <div className="page-content max-w-3xl mx-auto px-4 py-6 pb-20 w-full" data-testid="sources-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={goBackSafe}
            className="rounded-full"
            data-testid="sources-back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--theme-text)' }}>
              {t('generated.sourcespage_our_sources_0011')}
            </h1>
            <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('generated.sourcespage_what_eili_draws_7778')}
            </p>
          </div>
        </div>

        {/* Intro */}
        <div
          className="rounded-2xl p-5 mb-6"
          style={{
            background: 'rgba(16,185,129,0.08)',
            border: '1px solid rgba(16,185,129,0.25)',
          }}
        >
          <p className="text-sm leading-relaxed" style={{ color: 'var(--theme-text)' }}>
            <BookOpen className="inline h-4 w-4 mb-0.5 mr-1" style={{ color: '#10b981' }} />
            {t('generated.sourcespage_eili_s_historical_ff34')}
            {' '}<strong>{totalEntries > 0 ? `${totalEntries}+ verified entries` : 'verified scholarly entries'}</strong>{' '}
            {t('generated.sourcespage_spanning_ancient_manuscripts_41e3')}
          </p>
        </div>

        {/* Sources list */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: 'var(--theme-accent)' }} />
          </div>
        ) : (
          <div className="space-y-4">
            {sources.map((s) => (
              <div
                key={s.id}
                className="rounded-2xl p-5"
                style={{
                  background: 'var(--glass-bg)',
                  border: '1px solid var(--glass-border)',
                }}
                data-testid={`source-card-${s.id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                  <div>
                    <h2 className="text-base font-semibold leading-tight" style={{ color: 'var(--theme-text)' }}>
                      {s.name}
                    </h2>
                    <p className="text-[11px] mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>
                      {s.era}
                    </p>
                  </div>
                  <TypeBadge type={s.type} />
                </div>

                <p className="text-xs leading-relaxed mt-2" style={{ color: 'var(--theme-textSecondary)' }}>
                  {s.desc}
                </p>

                {s.works && s.works.length > 0 && (
                  <div className="mt-3">
                    <p className="text-[10px] uppercase tracking-wide font-medium mb-1" style={{ color: 'var(--theme-textSecondary)', letterSpacing: '0.06em' }}>
                      {t('generated.sourcespage_works_referenced_7c51')}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {s.works.map((w) => (
                        <span
                          key={w}
                          className="text-[11px] rounded-lg px-2 py-0.5"
                          style={{
                            background: 'var(--glass-bg-active, rgba(51,65,85,0.4))',
                            color: 'var(--theme-text)',
                            border: '1px solid var(--glass-border)',
                          }}
                        >
                          {w}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {s.coverage && (
                  <p className="text-[11px] mt-3 italic" style={{ color: 'var(--theme-textSecondary)' }}>
                    <span className="not-italic font-medium">{t('generated.sourcespage_coverage_7641')} </span>{s.coverage}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {note && (
          <p
            className="text-[11px] text-center mt-6 leading-relaxed"
            style={{ color: 'var(--theme-textSecondary)', opacity: 0.8 }}
          >
            {note}
          </p>
        )}
      </div>
    </div>
  );
}
